import { useState } from 'react';

import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import ReservationService from '~/services/ReservationService';

import { reportError } from '~/utils/reportError';

import type { useCapacityStep } from '../CapacityStep/useCapacityStep';
import type { usePackageSetupStep } from '../PackageSetupStep/usePackageSetupStep';
import type { usePropertyStep } from '../PropertyStep/usePropertyStep';
import type { useRatePlanStep } from '../RatePlanStep/useRatePlanStep';
import type { useRoomRatesStep } from '../RoomRatesStep/useRoomRatesStep';
import type { useRoomsStep } from '../RoomsStep/useRoomsStep';
import type { useTaxesFeesStep } from '../TaxesFeesStep/useTaxesFeesStep';
import type { SubmissionFailureRecovery } from '../types';

import type { RollbackErrorEntry } from './rollbackCreatedFlashResources';
import { submitFlashOfferDraft } from './submitFlashOfferDraft';
import type { SubmissionStage } from './submitFlashOfferDraft';

export interface UseSubmissionStepParams {
  capacityStep: ReturnType<typeof useCapacityStep>;
  packageSetupStep: ReturnType<typeof usePackageSetupStep>;
  propertyStep: ReturnType<typeof usePropertyStep>;
  ratePlanStep: ReturnType<typeof useRatePlanStep>;
  roomRatesStep: ReturnType<typeof useRoomRatesStep>;
  roomsStep: ReturnType<typeof useRoomsStep>;
  salesforceRooms: Array<SalesforceRoom>;
  taxesFeesStep: ReturnType<typeof useTaxesFeesStep>;
  trimmedSalesforceOfferId: string;
}

/**
 * Owns the Submission step (9): the one atomic "Create Flash offer" action that resolves every
 * prior step's draft into real resources, or rolls everything back on any failure.
 */
export function useSubmissionStep({
  capacityStep,
  packageSetupStep,
  propertyStep,
  ratePlanStep,
  roomRatesStep,
  roomsStep,
  salesforceRooms,
  taxesFeesStep,
  trimmedSalesforceOfferId,
}: UseSubmissionStepParams) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [rollbackErrors, setRollbackErrors] = useState<Array<RollbackErrorEntry>>([]);
  const [hasSucceeded, setHasSucceeded] = useState(false);
  const [failedStage, setFailedStage] = useState<SubmissionStage | 'property' | null>(null);
  const [flexibleNightsBlockedCount, setFlexibleNightsBlockedCount] = useState(0);
  const [packageUnmatchedCount, setPackageUnmatchedCount] = useState(0);
  const [updateFailurePackageIds, setUpdateFailurePackageIds] = useState<Array<string>>([]);
  const [packageLinkFailures, setPackageLinkFailures] = useState<Array<string>>([]);

  async function submit(): Promise<{ success: boolean }> {
    const propertyId = propertyStep.selectedProperty?.id;

    if (!propertyId) {
      setSubmitErrorMessage('Select or create a property before submitting.');
      setFailedStage('property');
      return { success: false };
    }

    setIsSubmitting(true);
    setSubmitErrorMessage(null);
    setRollbackErrors([]);
    setFailedStage(null);
    setFlexibleNightsBlockedCount(0);
    setPackageUnmatchedCount(0);
    setUpdateFailurePackageIds([]);
    setPackageLinkFailures([]);

    // catch/finally so a throw from submitFlashOfferDraft itself (as opposed to the success:false
    // paths it already returns) can never leave isSubmitting stuck true or fail silently.
    try {
      const result = await submitFlashOfferDraft({
        createRatePlansFromDrafts: ratePlanStep.createRatePlansFromDrafts,
        createSelectedRooms: roomsStep.createSelectedRooms,
        flexibleNightsById: packageSetupStep.flexibleNightsById,
        getProperty: (id) => ReservationService.getProperty(id),
        handleApplyDefaultCapacities: capacityStep.handleApplyDefaultCapacities,
        handleApplyRoomRates: roomRatesStep.handleApplyRoomRates,
        offerId: trimmedSalesforceOfferId,
        packageRatePlanIds: packageSetupStep.packageRatePlanIds,
        packageRoomTypeIds: packageSetupStep.packageRoomTypeIds,
        propertyId,
        roomDrafts: roomsStep.roomDrafts,
        roomRateRooms: roomRatesStep.roomRateRooms,
        salesforceRooms,
        selectedRoomRatePlanIds: roomRatesStep.selectedRoomRatePlanIds,
        taxesFeesDraft: taxesFeesStep.taxesFeesDraft,
        updatePropertyTaxesAndFees: (payload, id) => ReservationService.updatePropertyTaxesAndFees(payload, id),
        wasPropertyCreatedThisSession: propertyStep.createdProperty?.id === propertyId,
      });

      if (!result.success) {
        // Skipped when rollback itself left an error -- a resource may still exist server-side,
        // and clearing its step's marker would let the next attempt recreate a duplicate.
        if ((result.rollbackErrors ?? []).length === 0) {
          const stepsToReset: Array<SubmissionFailureRecovery> = [
            propertyStep,
            roomsStep,
            ratePlanStep,
            roomRatesStep,
            taxesFeesStep,
            packageSetupStep,
          ];

          stepsToReset.forEach((step) => step.resetAfterFailedSubmission());
        }

        setSubmitErrorMessage(result.errorMessage ?? 'Unable to create the Flash offer.');
        setRollbackErrors(result.rollbackErrors ?? []);
        setFailedStage(result.failedStage ?? null);
        setFlexibleNightsBlockedCount(result.flexibleNightsBlockedCount ?? 0);
        setPackageUnmatchedCount(result.packageUnmatchedCount ?? 0);
        setUpdateFailurePackageIds(result.updateFailurePackageIds ?? []);
        setPackageLinkFailures(result.packageLinkFailures ?? []);
        return { success: false };
      }

      setHasSucceeded(true);
      return { success: true };
    } catch (error) {
      // submitFlashOfferDraft catches its own known failure paths -- this only guards against a
      // genuinely unexpected throw, so the CM sees an error instead of a silently reset button.
      reportError(error);
      setSubmitErrorMessage('Unable to create the Flash offer. An unexpected error occurred.');
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  }

  function clearFailure() {
    setSubmitErrorMessage(null);
    setRollbackErrors([]);
    setFailedStage(null);
    setFlexibleNightsBlockedCount(0);
    setPackageUnmatchedCount(0);
    setUpdateFailurePackageIds([]);
    setPackageLinkFailures([]);
  }

  function reset() {
    setIsSubmitting(false);
    setHasSucceeded(false);
    clearFailure();
  }

  return {
    clearFailure,
    failedStage,
    flexibleNightsBlockedCount,
    hasSucceeded,
    hasUnresolvedRollbackErrors: rollbackErrors.length > 0,
    isSubmitting,
    packageLinkFailures,
    packageUnmatchedCount,
    reset,
    rollbackErrors,
    submit,
    submitErrorMessage,
    updateFailurePackageIds,
  };
}
